import { useState } from 'react';

interface Mission {
  id: string;
  title: string;
  description: string;
  tweetUrl?: string;
  targetAccount?: string;
  actions: string[];
  points: { like: number; follow: number; comment: number; share: number };
  completions?: Array<{
    action: string;
    verified: number;
    aiScore?: number;
    points_earned: number;
  }>;
}

interface SocialProps {
  connected: boolean;
  handle?: string;
  linkedSince?: string;
  todayPoints: number;
  missions?: Mission[];
  onStartAuth: () => Promise<{ success: boolean; authUrl?: string; error?: string }>;
  onUnlinkTwitter: () => Promise<{ success: boolean; error?: string }>;
  onVerifyMissionAction?: (missionId: string, action: string, proofTweetId?: string, commentText?: string) => Promise<{ success: boolean; pointsEarned?: number; aiScore?: number; error?: string }>;
}

// Per-action styling: icon, colors
const actionConfig: Record<string, { icon: React.ReactNode; color: string; bg: string; pointColor: string; label: string }> = {
  tweet: {
    icon: <UploadIcon />,
    color: 'text-social',
    bg: 'bg-social-light',
    pointColor: 'text-social',
    label: 'Post',
  },
  share: {
    icon: <UploadIcon />,
    color: 'text-social',
    bg: 'bg-social-light',
    pointColor: 'text-social',
    label: 'Share',
  },
  comment: {
    icon: <ChatIcon />,
    color: 'text-shield',
    bg: 'bg-shield-light',
    pointColor: 'text-shield',
    label: 'Post',
  },
  follow: {
    icon: <UserPlusIcon />,
    color: 'text-usdt',
    bg: 'bg-usdt-light',
    pointColor: 'text-usdt',
    label: 'Follow',
  },
  like: {
    icon: <HeartIcon />,
    color: 'text-kat',
    bg: 'bg-kat-light',
    pointColor: 'text-kat',
    label: 'Like',
  },
};

// Flatten missions into individual action cards
interface ActionCard {
  key: string;
  missionId: string;
  action: string;
  title: string;
  description: string;
  points: number;
  tweetUrl?: string;
  targetAccount?: string;
  completed: boolean;
  pointsEarned?: number;
  aiScore?: number;
}

function flattenMissions(missions: Mission[]): ActionCard[] {
  const cards: ActionCard[] = [];
  for (const mission of missions) {
    for (const action of mission.actions) {
      const completion = mission.completions?.find((c) => c.action === action);
      cards.push({
        key: `${mission.id}_${action}`,
        missionId: mission.id,
        action,
        title: mission.title,
        description: mission.description,
        points: mission.points[action as keyof typeof mission.points] || 0,
        tweetUrl: mission.tweetUrl,
        targetAccount: mission.targetAccount,
        completed: completion?.verified === 1,
        pointsEarned: completion?.points_earned,
        aiScore: completion?.aiScore,
      });
    }
  }
  return cards;
}

export function SocialEngagement({
  connected,
  handle,
  linkedSince,
  todayPoints,
  missions = [],
  onStartAuth,
  onUnlinkTwitter,
  onVerifyMissionAction,
}: SocialProps) {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [authError, setAuthError] = useState('');
  const [verifyingKey, setVerifyingKey] = useState<string | null>(null);
  const [commentCard, setCommentCard] = useState<string | null>(null);
  const [commentInput, setCommentInput] = useState('');
  const [taskError, setTaskError] = useState('');
  const [taskSuccess, setTaskSuccess] = useState('');
  // Optimistic: track locally completed actions so card flips instantly
  const [localCompleted, setLocalCompleted] = useState<Record<string, number>>({});

  const actionCards = flattenMissions(missions).map((card) => ({
    ...card,
    completed: card.completed || !!localCompleted[card.key],
    pointsEarned: card.pointsEarned ?? localCompleted[card.key],
  }));

  const handleAuth = async () => {
    setAuthError('');
    setIsAuthenticating(true);
    const result = await onStartAuth();
    if (result.success && result.authUrl) {
      window.location.href = result.authUrl;
    } else {
      setAuthError(result.error || 'Failed to start authentication');
      setIsAuthenticating(false);
    }
  };

  const handleUnlink = async () => {
    setIsUnlinking(true);
    await onUnlinkTwitter();
    setIsUnlinking(false);
  };

  // Click = done. Opens Twitter intent then immediately verifies.
  // For comments, opens the text input instead.
  const handleAction = async (card: ActionCard) => {
    if (card.action === 'comment') {
      // Comment needs text input — show the textarea
      setCommentCard(card.key);
      setCommentInput('');
      setTaskError('');
      return;
    }

    // Open Twitter intent
    if (card.action === 'like' || card.action === 'share') {
      window.open(card.tweetUrl || `https://x.com/${card.targetAccount || 'ternaboreal'}`, '_blank', 'width=600,height=500');
    } else if (card.action === 'follow' && card.targetAccount) {
      window.open(`https://x.com/intent/follow?screen_name=${card.targetAccount}`, '_blank', 'width=600,height=500');
    } else if (card.action === 'tweet') {
      const text = encodeURIComponent(`${card.description} @${card.targetAccount || 'ternaboreal'}`);
      window.open(`https://x.com/intent/tweet?text=${text}`, '_blank', 'width=600,height=500');
    }

    // Immediately verify — trust-based
    await verifyAction(card);
  };

  const verifyAction = async (card: ActionCard, commentText?: string) => {
    if (!onVerifyMissionAction) return;
    setTaskError('');
    setTaskSuccess('');
    setVerifyingKey(card.key);

    const result = await onVerifyMissionAction(
      card.missionId,
      card.action,
      undefined,
      commentText || undefined
    );
    setVerifyingKey(null);

    if (result.success) {
      setCommentCard(null);
      setCommentInput('');
      // Optimistic: mark card as completed immediately
      setLocalCompleted((prev) => ({ ...prev, [card.key]: result.pointsEarned ?? card.points }));
      let msg = `+${result.pointsEarned} pts earned!`;
      if (result.aiScore !== undefined) {
        msg += ` (AI Score: ${Math.round(result.aiScore)})`;
      }
      setTaskSuccess(msg);
      setTimeout(() => setTaskSuccess(''), 3000);
    } else {
      setTaskError(result.error || 'Failed to verify');
    }
  };

  return (
    <section>
      <h2 className="section-label">Social Engagement</h2>
      <div className="glass-card p-4 sm:p-10 border-l-[3px] border-l-social">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-ternoa flex items-center justify-center bg-social-light text-social">
              <XIcon />
            </div>
            <div>
              <div className="font-display text-base sm:text-xl font-semibold tracking-tight">X / Twitter</div>
              <div className="text-xs sm:text-sm text-muted">Earn KAT tokens for social engagement</div>
            </div>
          </div>
          {connected && (
            <div className="flex items-center gap-2 text-sm text-success font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              Connected
            </div>
          )}
        </div>

        {/* Connected account or connect button */}
        {connected ? (
          <div className="flex items-center gap-4 p-4 rounded-ternoa bg-social/5 border border-social/15 mb-6">
            <div className="w-9 h-9 rounded-ternoa bg-social-light flex items-center justify-center text-social font-bold text-sm">
              {handle?.[1]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1">
              <div className="font-display text-sm font-semibold">{handle}</div>
              <div className="text-xs text-success">Linked since {linkedSince}</div>
            </div>
            <button
              onClick={handleUnlink}
              disabled={isUnlinking}
              className="text-sm text-muted hover:text-gray-400 flex items-center gap-1 bg-transparent border-none cursor-pointer font-body transition-colors"
            >
              {isUnlinking ? 'Unlinking...' : 'Disconnect'}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="mb-6">
            <button
              onClick={handleAuth}
              disabled={isAuthenticating}
              className="btn-primary w-full flex items-center justify-center gap-3 py-4"
            >
              {isAuthenticating ? (
                <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <XIcon />
              )}
              {isAuthenticating ? 'Redirecting to Twitter...' : 'Connect with Twitter'}
            </button>
            {authError && (
              <div className="text-xs text-red-400 mt-2">{authError}</div>
            )}
            <div className="text-xs text-muted mt-2 text-center">
              Authenticate with Twitter to verify your account. No passwords stored.
            </div>
          </div>
        )}

        {/* Score */}
        <div className="flex flex-wrap items-baseline gap-2 mb-4 sm:mb-6">
          <span className="font-display text-xl sm:text-3xl font-bold">{todayPoints}</span>
          <span className="text-sm text-muted">social points earned this epoch</span>
          {taskSuccess && (
            <span className="text-sm text-success font-medium animate-fade-in-up">{taskSuccess}</span>
          )}
        </div>

        {/* Mission action cards */}
        {actionCards.length > 0 ? (
          <div className="flex flex-col gap-3">
            {actionCards.map((card) => {
              const cfg = actionConfig[card.action] || actionConfig.like;
              const isVerifying = verifyingKey === card.key;
              const isCommentOpen = commentCard === card.key;

              return (
                <div key={card.key}>
                  <div
                    className={`flex items-center justify-between p-3 sm:p-4 rounded-ternoa border transition-all ${
                      card.completed
                        ? 'border-success/20 bg-success/5 opacity-60'
                        : isVerifying
                          ? 'border-social/30 bg-social/5'
                          : 'border-border-subtle hover:bg-white/[0.02] hover:border-border-medium'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {card.completed ? (
                        <div className="w-8 h-8 rounded-ternoa bg-success/20 text-success flex items-center justify-center">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        </div>
                      ) : (
                        <div className={`w-8 h-8 rounded-ternoa flex items-center justify-center ${cfg.bg} ${cfg.color}`}>
                          {cfg.icon}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-300">{card.title}</div>
                        <div className="text-xs text-muted">{card.description}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className={`font-display text-sm font-semibold ${card.completed ? 'text-success' : cfg.pointColor}`}>
                        +{card.completed ? card.pointsEarned ?? card.points : card.points} pts
                      </span>
                      {!card.completed && connected && (
                        <button
                          onClick={() => handleAction(card)}
                          disabled={isVerifying}
                          className={`px-3 py-1.5 rounded-ternoa border text-xs font-medium transition-colors cursor-pointer font-body ${
                            cfg.color
                          } bg-transparent border-current/20 hover:bg-white/5 disabled:opacity-50`}
                        >
                          {isVerifying ? (
                            <span className="inline-block w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                          ) : (
                            cfg.label
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Comment input — only for comment actions */}
                  {isCommentOpen && !card.completed && (
                    <div className="flex gap-2 mt-2 ml-0 sm:ml-11 animate-fade-in-up">
                      <textarea
                        value={commentInput}
                        onChange={(e) => setCommentInput(e.target.value)}
                        placeholder="Write your comment here... AI will score its quality for bonus points"
                        className="flex-1 px-3 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-xs text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-accent/40 font-body"
                        rows={2}
                      />
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => verifyAction(card, commentInput)}
                          disabled={isVerifying || !commentInput.trim()}
                          className="px-3 py-2 rounded-ternoa bg-accent text-white text-xs font-semibold hover:bg-accent-hover disabled:opacity-50 transition-colors cursor-pointer border-none font-body"
                        >
                          {isVerifying ? 'Scoring...' : 'Submit'}
                        </button>
                        <button
                          onClick={() => { setCommentCard(null); setCommentInput(''); }}
                          className="px-3 py-2 rounded-ternoa text-muted hover:text-gray-400 text-xs bg-transparent border border-border-subtle cursor-pointer font-body"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-muted text-center py-6">
            No active missions yet. Check back soon!
          </div>
        )}

        {taskError && (
          <div className="text-xs text-red-400 mt-3">{taskError}</div>
        )}

        {/* Trust-based verification notice */}
        {connected && actionCards.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border-subtle">
            <div className="flex items-start gap-2.5 text-xs text-muted leading-relaxed">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-accent mt-0.5 shrink-0">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>
                We trust you by default — points are credited instantly. Our team runs daily checks to verify activity. Users found not completing actions will have their <span className="text-accent font-semibold">entire points balance reset to zero</span>.
              </span>
            </div>
          </div>
        )}

        {/* Not connected hint */}
        {!connected && actionCards.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border-subtle text-sm text-muted flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-social">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Connect your Twitter account above to start completing missions and earning KAT
          </div>
        )}
      </div>
    </section>
  );
}

// --- Icons ---
function XIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function UserPlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
    </svg>
  );
}
